import React, { useState, useEffect } from "react";
import { COUNTRIES } from "./countries";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { customColors } from "../../theme";
import { useTranslation } from "react-i18next";
import { trackButtonClickEvent } from "../../utils/tracking";

interface Country {
  code: string;
  name: string;
  file: string;
}

const FlagSelector: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const stored = localStorage.getItem("selectedCountry");
    if (stored) {
      setSelectedCountry(JSON.parse(stored));
    } else {
      const defaultCountry = COUNTRIES.find((c) => c.code === "us");
      setSelectedCountry(defaultCountry || null);
    }
  }, []);

  const handleSelect = (country: Country) => {
    setSelectedCountry(country);
    localStorage.setItem("selectedCountry", JSON.stringify(country));
    trackButtonClickEvent({
      label: "Country selected",
      extraParams: { country: country.toString() },
    });
    setOpen(false);
  };

  return (
    <>
      <IconButton
        onClick={() => {
          setOpen(true);
          trackButtonClickEvent({ label: "Country Selector Clicked" });
        }}
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          padding: 0,
          margin: "0 10px",
          backgroundColor: customColors.background,
          overflow: "hidden",
        }}
      >
        {selectedCountry && (
          <img
            src={selectedCountry.file}
            alt={selectedCountry.name}
            loading="lazy"
            width="36"
            height="36"
            style={{ objectFit: "cover" }}
          />
        )}
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px",
              backgroundColor: "#ffffff", // white background
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t("TEXT.SELECT_COUNTRY")}
          </Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          <Box
            display="flex"
            justifyContent="space-around"
            flexWrap="wrap"
            gap={2}
          >
            {COUNTRIES.map((country) => (
              <Box
                key={country.code}
                onClick={() => handleSelect(country)}
                sx={{
                  cursor: "pointer",
                  textAlign: "center",
                  borderRadius: "12px",
                  padding: "16px",
                  width: 140,
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "#f9f9f9",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <img
                  src={country.file}
                  alt={country.name}
                  width={80}
                  height={80}
                  loading="lazy"
                  style={{ borderRadius: "8px", marginBottom: 12 }}
                />
                <Typography variant="body2" fontWeight={500}>
                  {country.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FlagSelector;
